import React, { useEffect, useState } from "react";
import { CircleData, Step } from "../../../types/wizard";
import { useSnapshot } from "valtio";
import { checkIfColorIsBeingUsed, editColorFromDetails, saveEmblemData, setHoveredColor, wizardStore } from "../../../stores/wizard";
import { DeleteIcon, EditIcon } from "../../../utils/icons";
import ColorWarningModal from "../ColorWarningModal";
import { colorWarningStore } from "../../../stores/colorWarningStore";
import './../QuickView.css';

interface ColorDetailsProps {
  step: Step;
}

const ColorDetails: React.FC<ColorDetailsProps> = ({ step }) => {
  const wizSnap = useSnapshot(wizardStore);
  const maxColors = step?.numberOfColorsAllowedMax;
  const [colors, setColors] = useState<CircleData[]>([]);
  const allowWEItoChoose = wizSnap.designRequest[step.id]?.allowWEItoChoose || false;
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const minColorsRequired = step?.numberOfColorsAllowedMin ?? 0;
    const savedColors = wizSnap?.emblemData[step.id]?.items || [];
    setColors(savedColors);
    setMessage(
      minColorsRequired > 0 && minColorsRequired > (savedColors?.length ?? 0)
        ? `This design requires a minimum of ${minColorsRequired} colors.`
        : null
    );
  }, [wizSnap?.emblemData, step.id]);

  const saveData = (colorToRemove: CircleData) => {
    const { stepId, circle } = colorToRemove;
    const items = wizSnap.emblemData[stepId]?.items ?? [];
    //@ts-ignore
    const filteredItems = items.filter(item => item.circle?.id !== circle.id);
    saveEmblemData(stepId, { items: filteredItems });
  }

  const handleRemoveColor = (circleData: CircleData) => {
    if (checkIfColorIsBeingUsed(circleData)) {
      colorWarningStore.openModal(circleData, false, () => saveData(circleData));
    } else {
      saveData(circleData);
    }
  };

  const handleEditColor = (circleData: CircleData) => {
    if (checkIfColorIsBeingUsed(circleData)) {
      colorWarningStore.openModal(circleData, true, () => {
        setTimeout(() => {
          editColorFromDetails(circleData);
        }, 50);
      });
    } else {
      editColorFromDetails(circleData);
    }
  };

  const handleMouseEnter = (circleData: CircleData) => {
    const { circle } = circleData;

    if (circle?.id)
      setHoveredColor(circle?.id)
  };

  const handleMouseLeave = () => {
    setHoveredColor(0)
  };

  return (
    <>
      {
        allowWEItoChoose ? null :
          <>
            <div className="w-full mb-4">
              {!colors?.length &&
                <div className="grid grid-flow-col gap-4 flex flex-row items-center">
                  <div className="col-span-1 border h-7 w-7 rounded-[50%] border-dashed border-[#a7acaf]" />
                  <div className="col-span-3 flex flex-col">
                    <span className="font-bold text-lg my-4">No Colors Selected</span>
                    <span className="">Click anywhere on the design to specify color.</span>
                  </div>
                </div>
              }
              {colors?.length && maxColors === colors?.length ?
                <span className="text-error pt-2 text-sm">You have entered the maximum number of colors: {maxColors}</span>
                : null
              }
              <div className="">
                {/* selected colors */}
                {colors?.length !== 0 && <div className="col-span-3 flex flex-col">
                  <span className="font-bold text-lg my-4">{colors?.length} Colors Selected</span>
                </div>}
                {colors?.map((color, index) => {
                  if (color.option.htmlColor)
                    return <div key={color.circle?.id || index}
                      className="flex flex-row justify-between items-center gap-x-[1em]">
                      <div
                        className="flex flex-row justify-start items-center">
                        <div
                          className="border h-7 w-7 rounded-[50%] border border-[#a7acaf] mr-3"
                          key={color.option.id}
                          style={{ backgroundColor: color.color }} />
                        <label htmlFor={`${color.option.id}`} className="">
                          {color.option.name}
                        </label>
                      </div>
                      <div
                        className="flex flex-row justify-end gap-2"
                        onMouseEnter={() => handleMouseEnter(color)}
                        onMouseLeave={handleMouseLeave}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditColor(color) }}
                          className="bg-white outline-none border-0 hover:outline-none focus:outline-none px-1">
                          <EditIcon />
                        </button>
                        <button
                          onClick={() => handleRemoveColor(color)}
                          className="bg-white outline-none border-0 hover:outline-none focus:outline-none px-1">
                          <DeleteIcon />
                        </button>
                      </div>
                    </div>
                })}
              </div>
            </div>
            <hr className="dashed-title" />
            {maxColors !== 0 &&
              <div className="mb-2 mt-4 mb-4 text-sm">
                {`A maximum number of ${maxColors} colors can be included. Add as many markers as needed.`}
              </div>
            }
            {message && <span className="text-error pt-2 text-sm">{message}</span>}
          </>
      }

      {/* Modal Warning Message */}
      <ColorWarningModal />
    </>
  );
};

export default ColorDetails;
